import React, {useEffect} from "react";
import {useFormContext} from "react-hook-form";
import {ClientReportSubmitInput, isProgressReportFamily, ReportServiceType, serviceTypeMapping, serviceTypeMappedToHourFields} from "../domain";
import {ModalTextAreaInput} from "../form-components/ModalTextAreaInput";
import {LatestProviderTransitionRequestCase} from "./useLatestOfferedHoursForClientReport";
import {shouldShowHourInputs} from "../form-components/auth-hours/view-domains";

export type TransitionHours = {
    caregiverMidLevelHoursMin: number | null;
    caregiverMidLevelHoursMax: number | null;
    caregiverHighLevelHoursMin: number | null;
    caregiverHighLevelHoursMax: number | null;
    practitionerParaprofessionalHoursMin: number | null;
    practitionerParaprofessionalHoursMax: number | null;
    practitionerMidLevelHoursMin: number | null;
    practitionerMidLevelHoursMax: number | null;
    practitionerHighLevelHoursMin: number | null;
    practitionerHighLevelHoursMax: number | null;
    practitionerGroupCareHoursMin: number | null;
    practitionerGroupCareHoursMax: number | null;
};

function mapTransitionDataToHours(data: LatestProviderTransitionRequestCase): TransitionHours {
    return {
        caregiverMidLevelHoursMin: data.caregiverMediatedMidLevelMin,
        caregiverMidLevelHoursMax: data.caregiverMediatedMidLevel,
        caregiverHighLevelHoursMin: data.caregiverMediatedHighLevelMin,
        caregiverHighLevelHoursMax: data.caregiverMediatedHighLevel,
        practitionerParaprofessionalHoursMin: data.pracMediatedDirectCareBeneficial,
        practitionerParaprofessionalHoursMax: data.pracMediatedDirectCareOptimal,
        practitionerMidLevelHoursMin: data.pracMediatedMidLevelBeneficial,
        practitionerMidLevelHoursMax: data.pracMediatedMidLevelOptimal,
        practitionerHighLevelHoursMin: data.pracMediatedHighLevelBeneficial,
        practitionerHighLevelHoursMax: data.pracMediatedHighLevelOptimal,
        practitionerGroupCareHoursMin: data.pracMediatedGroupCareBeneficial,
        practitionerGroupCareHoursMax: data.pracMediatedGroupCareOptimal,
    };
}

const TRANSITION_HOUR_FIELDS = new Set<string>([
    'caregiverMidLevelHoursMin', 'caregiverMidLevelHoursMax',
    'caregiverHighLevelHoursMin', 'caregiverHighLevelHoursMax',
    'practitionerParaprofessionalHoursMin', 'practitionerParaprofessionalHoursMax',
    'practitionerMidLevelHoursMin', 'practitionerMidLevelHoursMax',
    'practitionerHighLevelHoursMin', 'practitionerHighLevelHoursMax',
    'practitionerGroupCareHoursMin', 'practitionerGroupCareHoursMax',
]);

function getRelevantFields(serviceTypes: ReportServiceType[]): Set<keyof TransitionHours> {
    const fields = new Set<keyof TransitionHours>();
    serviceTypes.forEach((st) => {
        const mapped = serviceTypeMappedToHourFields.get(st) ?? [];
        mapped.forEach((f) => {
            if (TRANSITION_HOUR_FIELDS.has(f)) {
                fields.add(f as keyof TransitionHours);
            }
        });
    });
    return fields;
}

export function hasAllRequiredHours(
    formValues: Partial<ClientReportSubmitInput>,
    relevantFields: Set<keyof TransitionHours>,
): boolean {
    return Array.from(relevantFields).every(
        (field) => field.endsWith('Min') || (formValues as Record<string, unknown>)[field] != null,
    );
}

export function hasMismatch(
    formValues: Partial<ClientReportSubmitInput>,
    transitionHours: TransitionHours,
    relevantFields: Set<keyof TransitionHours>,
    formServiceTypes: ReportServiceType[],
    transitionServiceTypes: ReportServiceType[],
): boolean {
    const missingServiceType = transitionServiceTypes.some(
        (transitionServiceType) => !formServiceTypes.includes(transitionServiceType),
    );
    if (missingServiceType) {
        return true;
    }

    for (const field of Array.from(relevantFields)) {
        const transitionVal = transitionHours[field];
        const formVal = (formValues as Record<string, unknown>)[field];
        if (field.endsWith('Min') && transitionVal == null) continue;
        if (transitionVal == null && formVal == null) continue;
        if (formVal == null || transitionVal !== Number(formVal)) return true;
    }
    return false;
}

type ReadOnlyHourRowProps = {
    label: string;
    min: number | null;
    max: number | null;
};

const READ_ONLY_INPUT_CLASS =
    "block w-1/3 bg-gray-100 border border-gray-300 p-2 rounded shadow leading-tight cursor-default select-none pointer-events-none";

function ReadOnlyHourRow({label, min, max}: ReadOnlyHourRowProps) {
    if (min == null && max == null) return null;
    return (
        <div className="w-full flex">
            <div className="w-1/2 m-2">
                <div className="flex items-center">
                    <div className="w-2/3 text-right pr-3">{label} - Min</div>
                    <input className={READ_ONLY_INPUT_CLASS} readOnly tabIndex={-1} value={min ?? ''} />
                </div>
            </div>
            <div className="w-1/2 m-2">
                <div className="flex items-center">
                    <div className="w-2/3 text-right pr-3">{label} - Max</div>
                    <input className={READ_ONLY_INPUT_CLASS} readOnly tabIndex={-1} value={max ?? ''} />
                </div>
            </div>
        </div>
    );
}

function mapTransitionServiceTypes(rawServiceTypes: string[]): ReportServiceType[] {
    return rawServiceTypes
        .map((raw) => Object.values(ReportServiceType).includes(raw as ReportServiceType)
            ? (raw as ReportServiceType)
            : null
        )
        .filter((st): st is ReportServiceType => st != null);
}

type Props = {
    transitionRequestData: LatestProviderTransitionRequestCase;
};

export function TransitionRequestMismatchWarning({transitionRequestData}: Props) {
    const {watch, getValues, setValue} = useFormContext<ClientReportSubmitInput>();
    const formServiceTypes = watch('serviceType') as ReportServiceType[];
    const reportType = watch('reportType') as string;

    const transitionServiceTypes = mapTransitionServiceTypes(transitionRequestData.serviceTypes ?? []);

    const canEvaluateMismatch = isProgressReportFamily(reportType)
        && transitionServiceTypes.length > 0
        && !!formServiceTypes?.length;

    const transitionHours = canEvaluateMismatch
        ? mapTransitionDataToHours(transitionRequestData)
        : null;
    const relevantFields = canEvaluateMismatch
        ? getRelevantFields(formServiceTypes)
        : new Set<keyof TransitionHours>();
    const relevantHourValues = watch(Array.from(relevantFields));
    const relevantFormValues = Object.fromEntries(
        Array.from(relevantFields).map((field, index) => [field, relevantHourValues[index]]),
    ) as Partial<ClientReportSubmitInput>;
    const hasCompletedRequiredHours = canEvaluateMismatch
        && hasAllRequiredHours(relevantFormValues, relevantFields);
    const mismatch = hasCompletedRequiredHours
        && transitionHours != null
        && hasMismatch(
            getValues(),
            transitionHours,
            relevantFields,
            formServiceTypes,
            transitionServiceTypes,
        );

    useEffect(() => {
        if (!mismatch) {
            setValue('recommendationDifferenceRationale', null as any);
        }
    }, [mismatch, setValue]);

    if (!mismatch) return null;

    const showCaregiverHours = shouldShowHourInputs(transitionServiceTypes, [
        'caregiverMidLevelHoursMin', 'caregiverMidLevelHoursMax',
        'caregiverHighLevelHoursMin', 'caregiverHighLevelHoursMax',
    ]);

    const showParaprofessionalHours = shouldShowHourInputs(transitionServiceTypes, [
        'practitionerParaprofessionalHoursMin', 'practitionerParaprofessionalHoursMax',
    ]);

    const showPractitionerMidHours = shouldShowHourInputs(transitionServiceTypes, [
        'practitionerMidLevelHoursMin', 'practitionerMidLevelHoursMax',
    ]);

    const showPractitionerHighHours = shouldShowHourInputs(transitionServiceTypes, [
        'practitionerHighLevelHoursMin', 'practitionerHighLevelHoursMax',
    ]);

    const showGroupCareHours = shouldShowHourInputs(transitionServiceTypes, [
        'practitionerGroupCareHoursMin', 'practitionerGroupCareHoursMax',
    ]);

    const serviceTypeMappingLoose = serviceTypeMapping as Record<string, string>;
    const serviceTypeLabel = transitionServiceTypes
        .map((st) => serviceTypeMappingLoose[st] ?? st)
        .join(', ');

    return (
        <div className="border border-orange-400 rounded p-4 mt-4 bg-orange-50">
            <div className="flex items-start gap-2 mb-3">
                <span className="text-orange-500 text-lg leading-tight">⚠</span>
                <span className="text-orange-700 font-semibold text-sm">
                    Clinical Recommendation doesn't match the recommendation submitted in the client's transfer request #{transitionRequestData.caseNumber}
                </span>
            </div>

            <hr className="border-orange-300 mb-3"/>

            <div className="font-bold pb-2 text-sm">Clinical Recommendations</div>

            {serviceTypeLabel && (
                <div className="flex text-sm pb-2 items-center">
                    <div className="w-1/4 text-right pr-3">Service Type</div>
                    <input
                        className="w-1/3 bg-gray-100 border border-gray-300 p-2 rounded shadow leading-tight cursor-default select-none pointer-events-none"
                        readOnly
                        tabIndex={-1}
                        value={serviceTypeLabel}
                    />
                </div>
            )}

            {showCaregiverHours && (
                <div className="pb-2 text-sm">
                    <div className="italic">Caregiver Mediated Treatment (hours per month)</div>
                    <ReadOnlyHourRow
                        label="Mid-level Hours"
                        min={transitionHours.caregiverMidLevelHoursMin}
                        max={transitionHours.caregiverMidLevelHoursMax}
                    />
                    <ReadOnlyHourRow
                        label="High-level Hours"
                        min={transitionHours.caregiverHighLevelHoursMin}
                        max={transitionHours.caregiverHighLevelHoursMax}
                    />
                </div>
            )}

            {(showParaprofessionalHours || showPractitionerMidHours || showPractitionerHighHours || showGroupCareHours) && (
                <div className="pb-2 text-sm">
                    <div className="italic">Practitioner Mediated Treatment (hours per month)</div>
                    {showParaprofessionalHours && (
                        <ReadOnlyHourRow
                            label="Paraprofessional Hours"
                            min={transitionHours.practitionerParaprofessionalHoursMin}
                            max={transitionHours.practitionerParaprofessionalHoursMax}
                        />
                    )}
                    {showPractitionerMidHours && (
                        <ReadOnlyHourRow
                            label="Mid-level Hours"
                            min={transitionHours.practitionerMidLevelHoursMin}
                            max={transitionHours.practitionerMidLevelHoursMax}
                        />
                    )}
                    {showPractitionerHighHours && (
                        <ReadOnlyHourRow
                            label="High-level Hours"
                            min={transitionHours.practitionerHighLevelHoursMin}
                            max={transitionHours.practitionerHighLevelHoursMax}
                        />
                    )}
                    {showGroupCareHours && (
                        <ReadOnlyHourRow
                            label="Group Care Hours"
                            min={transitionHours.practitionerGroupCareHoursMin}
                            max={transitionHours.practitionerGroupCareHoursMax}
                        />
                    )}
                </div>
            )}

            <ModalTextAreaInput<ClientReportSubmitInput>
                label="Recommendation Difference Rationale"
                labelPosition="left"
                field="recommendationDifferenceRationale"
                disabled={false}
                required
                requiredMessage="Please enter rationale for recommendation difference"
                rowsNum={3}
                maxLength={4000}
                showCharacterCount
                shouldStopInputOnMaxLength={false}
                labelClassName="text-sm"
            />
        </div>
    );
}
